import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import type { InstitutionBranchOperations } from '@/features/institution/branch-operations/schema';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsAnswers, useInstitutionDetailsMutation, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import {
  institutionDetailsBranchOperationsDifferentJurisdictionSchema,
  institutionDetailsBranchOperationsJurisdictionSchema,
  institutionDetailsBranchOperationsSchema,
} from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { isJurisdictionPermitted } from '@/utils';
import { InstitutionBranchOperationsFieldsetDifferentJurisdiction, InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend } from './fieldsets/different-jurisdiction';
import { InstitutionBranchOperationsFieldsetJurisdiction, InstitutionBranchOperationsFieldsetJurisdictionLegend } from './fieldsets/jurisdiction';

const FORM_STEPS = {
  DIFFERENT_JURISDICTION: {
    Component: InstitutionBranchOperationsFieldsetDifferentJurisdiction,
    Legend: InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend,
    schema: institutionDetailsBranchOperationsDifferentJurisdictionSchema,
  },
  JURISDICTION: {
    Component: InstitutionBranchOperationsFieldsetJurisdiction,
    Legend: InstitutionBranchOperationsFieldsetJurisdictionLegend,
    schema: institutionDetailsBranchOperationsJurisdictionSchema,
  },
} satisfies Record<string, FormStep<InstitutionBranchOperations>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionBranchOperationsFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionBranchOperations>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'branchOperations', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionBranchOperationsFormCurrentStep.displayName = 'InstitutionBranchOperationsFormCurrentStep';

const InstitutionBranchOperationsFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionBranchOperationsFormCurrentStepLegend.displayName = 'InstitutionBranchOperationsFormCurrentStepLegend';

const InstitutionBranchOperationsFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('branchOperations');
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(prevDetailsRoute, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionBranchOperationsFormBackButton.displayName = 'InstitutionBranchOperationsFormBackButton';

const InstitutionBranchOperationsFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionBranchOperationsFormFooter.displayName = 'InstitutionBranchOperationsFormFooter';

const InstitutionBranchOperationsForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'branchOperations');
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('branchOperations');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow || isLoadingCountries;

  const onSubmit = async (data: InstitutionBranchOperations, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { branchOperations: await institutionDetailsBranchOperationsSchema.parseAsync(data) };

    await mutateAsync(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        const { differentJurisdiction, countryOfBranchOperations } = data;

        if (differentJurisdiction === 'YES' && !isJurisdictionPermitted(countryOfBranchOperations, countries ?? {})) {
          navigate(`${onboardingPath}/branch-operations/forbidden-jurisdiction/${countryOfBranchOperations}`, { replace: true });
        } else {
          navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
        }
      },
    });
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.branchOperations ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionBranchOperationsFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionBranchOperationsFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionBranchOperations) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionBranchOperationsFormCurrentStep />
          <InstitutionBranchOperationsFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionBranchOperationsForm.displayName = 'InstitutionBranchOperationsForm';

export default InstitutionBranchOperationsForm;
